import { useRef, useState } from 'react'
import { MinimalAlert, MinimalButton } from '@ovasabi/ui-minimal'
import { commandPayload, commands } from '../../lib/runtime'
import { useDishSnapshot, useVendorProfileSnapshot } from '../../generated/prototypeRuntime'
import * as S from '../ops/OpsStyle'
import type { Row } from '../ops/opsData'
import { campaignSchema, campaignStatus, type Campaign, type CampaignStatus } from './promotionModel'
import { CampaignForm } from './CampaignForm'
import { CampaignDetail } from './CampaignDetail'
import { useCampaigns } from './useCampaigns'

const statuses: CampaignStatus[] = ['Active', 'Scheduled', 'Paused', 'Expired', 'Fully redeemed']

export const PromotionsPanel = ({ chefProfileId, initialDish = '' }: { chefProfileId?: string; initialDish?: string }) => {
  const { campaigns, setCampaigns, loading, loaded, loadError, offset, setOffset, now, refresh } = useCampaigns(chefProfileId)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [filter, setFilter] = useState<CampaignStatus | ''>('')
  const [selectedId, setSelectedId] = useState('')
  const [creating, setCreating] = useState(Boolean(initialDish))
  const [busy, setBusy] = useState(false)
  const pending = useRef(false)
  const dishes = useDishSnapshot() as { records: readonly Row[] }
  const vendors = useVendorProfileSnapshot() as { records: readonly Row[] }
  const selected = campaigns.find((c) => c.id === selectedId)
  const toggle = async (campaign: Campaign) => {
    if (pending.current) return
    pending.current = true; setBusy(true); setError(''); setNotice('')
    try {
      const updated = campaignSchema.parse(commandPayload(await commands.promotions.setStatus(campaign.id, !campaign.is_active)).campaign)
      if (updated.id !== campaign.id) throw new Error('Campaign update was not confirmed.')
      setCampaigns((rows) => rows.map((row) => row.id === updated.id ? updated : row))
      setNotice(updated.is_active ? 'Campaign resumed.' : 'Campaign paused.')
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not update campaign.') }
    finally { pending.current = false; setBusy(false) }
  }

  const archive = async (campaign: Campaign) => {
    if (pending.current) return
    pending.current = true; setBusy(true); setError(''); setNotice('')
    try {
      const archived = campaignSchema.parse(commandPayload(await commands.promotions.archive(campaign.id)).campaign)
      if (archived.id !== campaign.id) throw new Error('Campaign removal was not confirmed.')
      setCampaigns((rows) => rows.filter((row) => row.id !== archived.id))
      setSelectedId('')
      setNotice(`Campaign removed. ${archived.code} no longer applies at checkout and cannot be reused.`)
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not remove campaign.') }
    finally { pending.current = false; setBusy(false) }
  }

  const scope = (c: Campaign) => {
    const vendor = vendors.records.find((v) => String(v.profile_id) === c.merchant_profile_id)
    const dish = dishes.records.find((d) => String(d.id) === c.dish_id)
    if (c.dish_id) return `${String(dish?.name ?? dish?.title ?? c.dish_id)} · ${String(vendor?.display_name ?? 'Kitchen')}`
    return c.merchant_profile_id ? String(vendor?.display_name ?? 'Kitchen campaign') : 'All kitchens'
  }
  const visible = campaigns.filter((c) => !filter || campaignStatus(c, now) === filter)

  return <>
    <S.Card>
      <S.CardTitle>Promo-code campaigns</S.CardTitle>
      <S.Note>Discounts apply to food only, per kitchen order. The larger item discount applies; benefits do not stack.</S.Note>
      <MinimalButton onClick={() => { setError(''); setCreating(true) }}>Create campaign</MinimalButton>
      <MinimalButton variant="ghost" disabled={loading || busy} onClick={() => refresh()}>Refresh</MinimalButton>
    </S.Card>
    {notice && <MinimalAlert tone="success" title="Saved">{notice}</MinimalAlert>}
    {(error || loadError) && <MinimalAlert tone="danger" title="Promotion action failed">{error || loadError}<MinimalButton onClick={() => refresh()}>Retry</MinimalButton></MinimalAlert>}
    {loading ? <p role="status">Loading campaigns…</p> : loaded && <>
      <S.StatRow>{statuses.map((status) => <S.StatButton key={status} type="button" aria-pressed={filter === status} onClick={() => setFilter(filter === status ? '' : status)}>
        <S.StatValue>{campaigns.filter((c) => campaignStatus(c, now) === status).length}</S.StatValue><S.StatLabel>{status}</S.StatLabel>
      </S.StatButton>)}</S.StatRow>
      <S.Note>{filter || 'All campaigns'} · page {offset / 50 + 1}. Status counts describe this page.</S.Note>
      {visible.length === 0 && <S.Empty>No campaigns match. Create a campaign or select another status.</S.Empty>}
      <S.StatRow>{visible.map((c) => <S.StatButton key={c.id} type="button" onClick={() => setSelectedId(c.id)}>
        <S.Pill $tone={campaignStatus(c, now) === 'Active' ? 'ok' : 'flat'}>{campaignStatus(c, now)}</S.Pill>
        <S.StatValue>{c.discount_bps / 100}% off</S.StatValue><strong>{c.name}</strong>
        <S.StatLabel>{c.code} · {scope(c)}</S.StatLabel>
        <S.Note>{c.redemption_count} / {c.max_redemptions || 'unlimited'} checkouts · View details →</S.Note>
      </S.StatButton>)}</S.StatRow>
      <S.Nav><MinimalButton variant="secondary" disabled={offset === 0} onClick={() => setOffset((n) => Math.max(0, n - 50))}>Previous</MinimalButton>
        <MinimalButton variant="secondary" disabled={campaigns.length < 50 || offset >= 100000} onClick={() => setOffset((n) => n + 50)}>Next</MinimalButton></S.Nav>
    </>}
    {creating && <CampaignForm chefProfileId={chefProfileId} initialDish={initialDish} dishes={dishes.records} vendors={vendors.records} onClose={() => setCreating(false)} onCreated={() => {
      setCreating(false); setOffset(0); setFilter(''); refresh(); setNotice('Campaign created. Share its code with customers.')
    }} />}
    {selected && <CampaignDetail selected={selected} scope={scope(selected)} now={now} busy={busy} error={error} onClose={() => setSelectedId('')} onToggle={() => void toggle(selected)} onArchive={() => void archive(selected)} />}
  </>
}
